import { calculateDTE } from "@/utils/option-symbol-utils";

export const POSITION_INTENTS = [
  "active_trade",
  "long_term",
  "covered_call_long_term",
  "ignore",
] as const;

export type PositionIntent = (typeof POSITION_INTENTS)[number];

export const DEFAULT_POSITION_INTENT: PositionIntent = "active_trade";
export const LONG_TERM_EXPIRATION_DTE_THRESHOLD = 360;

export function isPositionIntent(value: unknown): value is PositionIntent {
  return (
    typeof value === "string" &&
    (POSITION_INTENTS as readonly string[]).includes(value)
  );
}

export function getPositionIntentFlags(intent: PositionIntent): {
  includeInActiveAllocation: boolean;
  includeInRecommendationExposure: boolean;
} {
  switch (intent) {
    case "long_term":
    case "ignore":
      return {
        includeInActiveAllocation: false,
        includeInRecommendationExposure: false,
      };
    case "covered_call_long_term":
    case "active_trade":
      return {
        includeInActiveAllocation: true,
        includeInRecommendationExposure: true,
      };
  }
}

export function getIntentFromPositionLike(
  position: Record<string, unknown>,
): PositionIntent {
  const explicit =
    position["position-intent"] ??
    position.positionIntent ??
    position.position_intent;

  if (isPositionIntent(explicit)) {
    return explicit;
  }
  if (typeof explicit === "string") {
    try {
      const parsed = JSON.parse(explicit);
      if (isPositionIntent(parsed)) {
        return parsed;
      }
    } catch {
      // Not JSON encoded; fall through to legacy flags.
    }
  }

  const legacyLongTerm = position["is-long-term"] ?? position.isLongTerm;
  if (legacyLongTerm === true || legacyLongTerm === "true") {
    return "long_term";
  }

  const symbol = position.symbol;
  if (typeof symbol === "string") {
    const dte = calculateDTE(symbol);
    if (dte !== null && dte > LONG_TERM_EXPIRATION_DTE_THRESHOLD) {
      return "long_term";
    }
  }

  return DEFAULT_POSITION_INTENT;
}

export function includePositionInActiveAllocation(
  position: Record<string, unknown>,
): boolean {
  return getPositionIntentFlags(getIntentFromPositionLike(position))
    .includeInActiveAllocation;
}

export function includePositionInRecommendationExposure(
  position: Record<string, unknown>,
): boolean {
  return getPositionIntentFlags(getIntentFromPositionLike(position))
    .includeInRecommendationExposure;
}
